import React, { useState, useEffect } from 'react';
import axios from 'axios';
import APIServiceDetails from './APIServiceDetails';

const APIServices = () => {
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedService, setSelectedService] = useState(null);

    useEffect(() => {
        fetchServices();
    }, []);

    const fetchServices = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/client/api-services`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setServices(response.data.services);
        } catch (error) {
            console.error('Error fetching API services:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            </div>
        );
    }

    return (
        <div className="px-4 sm:px-6 lg:px-8 py-6">
            {/* Header */}
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900">API Services</h1>
                <p className="mt-2 text-sm text-gray-700">
                    Manage and monitor your API integrations
                </p>
            </div>

            {/* Services Grid */}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {services.map((service) => (
                    <div
                        key={service.id}
                        className={`relative rounded-lg border ${service.enabled
                            ? 'border-green-300 bg-green-50'
                            : 'border-gray-300 bg-white'
                            } p-6 shadow-sm hover:shadow-md transition-shadow cursor-pointer`}
                        onClick={() => setSelectedService(service)}
                    >
                        {/* Status Badge */}
                        <div className="absolute top-4 right-4">
                            <span
                                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${service.enabled
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-gray-100 text-gray-800'
                                    }`}
                            >
                                {service.enabled ? 'Active' : 'Inactive'}
                            </span>
                        </div>

                        {/* Icon */}
                        <div className="text-4xl mb-3">{service.icon}</div>

                        {/* Service Info */}
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                            {service.name}
                        </h3>
                        <p className="text-sm text-gray-600 mb-4">
                            {service.description}
                        </p>

                        {/* Footer */}
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-500">
                                {service.endpoints_count} endpoints
                            </span>
                            {service.enabled && service.has_api_key && (
                                <span className="text-green-600 font-medium">
                                    🔑 Key Active
                                </span>
                            )}
                        </div>

                        {/* Call to Action */}
                        {service.enabled ? (
                            <button
                                className="mt-4 w-full bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 transition-colors"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedService(service);
                                }}
                            >
                                View Details
                            </button>
                        ) : (
                            <div className="mt-4 w-full bg-gray-100 text-gray-500 py-2 px-4 rounded-md text-center">
                                Not Enabled
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* No Services */}
            {services.length === 0 && (
                <div className="text-center py-12">
                    <p className="text-gray-500">No API services available</p>
                </div>
            )}
            {/* Service Details Modal */}
            {selectedService && (
                <APIServiceDetails
                    serviceId={selectedService.id}
                    onClose={() => setSelectedService(null)}
                />
            )}
        </div>
    );
};

export default APIServices;
